import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ImageUpload, inputCls } from "@/admin/fields";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const FIELDS = [
  ["club_name", "Club Name"], ["abbreviation", "Abbreviation"], ["address", "Address"],
  ["email_admin", "Admin Email"], ["email_organiser", "Organiser Email"],
  ["phone_director", "Director Phone"], ["phone_organiser", "Organiser Phone"], ["phone_admin", "Admin Phone"],
  ["whatsapp", "WhatsApp Number"], ["facebook", "Facebook URL"], ["instagram", "Instagram URL"], ["twitter", "Twitter/X URL"],
  ["seo_title", "SEO Title"], ["announcement", "Site Announcement (banner)"],
  ["join_form_url", "Online Registration Form URL (Google Form)"],
];
const TEXTAREAS = [["seo_description", "SEO Description"], ["sponsorship_message", "Sponsorship Message"], ["footer_text", "Footer Text"]];

export default function SettingsManager() {
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/public/settings").then((r) => setS(r.data || {})); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); refresh(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return <Loader2 className="mx-auto mt-20 h-7 w-7 animate-spin text-slate-400" />;
  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-extrabold uppercase text-white">Website Settings</h1><p className="text-sm text-slate-400">Club details, contacts, social links and SEO defaults.</p></div>
        <button onClick={save} disabled={saving} data-testid="settings-save-button" className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-5 py-2 text-sm font-bold uppercase text-[#0A1626]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
      </div>
      <div className="mt-6 space-y-6">
        <div className="rounded-xl border border-slate-700 bg-[#151F30] p-5">
          <label className="mb-1 block text-xs font-bold uppercase text-slate-400">Club Logo</label>
          <ImageUpload value={s.logo_url} onChange={(v) => set("logo_url", v)} testid="settings-logo-upload" />
        </div>
        <div className="grid gap-4 rounded-xl border border-slate-700 bg-[#151F30] p-5 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <div key={k}><label className="mb-1 block text-xs font-bold uppercase text-slate-400">{label}</label><input className={inputCls} value={s[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`settings-${k}`} /></div>
          ))}
          <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Registration Status</label>
            <Select value={s.registration_status || "enquiries_open"} onValueChange={(v) => set("registration_status", v)}>
              <SelectTrigger className={inputCls} data-testid="settings-registration-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="enquiries_open">Enquiries Open</SelectItem>
                <SelectItem value="open">Registration Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {TEXTAREAS.map(([k, label]) => (
            <div key={k} className="sm:col-span-2"><label className="mb-1 block text-xs font-bold uppercase text-slate-400">{label}</label><textarea rows={3} className={inputCls} value={s[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`settings-${k}`} /></div>
          ))}
          <div className="sm:col-span-2 rounded-lg border border-slate-700 p-4">
            <div className="flex items-center justify-between">
              <div><div className="text-sm font-bold uppercase text-slate-200">Email notifications</div><div className="text-xs text-slate-400">Email the club when a new enquiry is submitted (enquiries always appear in the dashboard too).</div></div>
              <Switch checked={!!s.email_notifications_enabled} onCheckedChange={(v) => set("email_notifications_enabled", v)} data-testid="settings-email-notifications" />
            </div>
            <div className="mt-3"><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Notification Recipient Email</label><input type="email" className={inputCls} placeholder="Defaults to the admin email if left blank" value={s.notify_email || ""} onChange={(e) => set("notify_email", e.target.value)} data-testid="settings-notify-email" /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
